import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { HelpCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { lookup } from '../../lib/glossary';

/**
 * Termine quant con popover esplicativo on hover/click.
 *
 * Uso:
 *   <GlossaryTerm term="Sharpe">Sharpe</GlossaryTerm>
 *   <GlossaryTerm term="Sharpe" showIcon /> Sharpe <icon=help>
 *   <GlossaryTerm term="Sharpe" asLabel>Sharpe Ratio</GlossaryTerm>
 *
 * Se il termine non è nel glossario, renderizza il children senza decoration.
 */
const directionIcon = (dir) => {
  if (dir === 'higher_better') return <TrendingUp className="w-3 h-3 text-emerald-600" />;
  if (dir === 'lower_better') return <TrendingDown className="w-3 h-3 text-emerald-600" />;
  return <Minus className="w-3 h-3 text-muted-foreground" />;
};

const directionLabel = (dir) => {
  if (dir === 'higher_better') return 'più alto = meglio';
  if (dir === 'lower_better') return 'più basso = meglio';
  return 'neutrale';
};

const GlossaryTerm = ({ term, children, showIcon = false, asLabel = false, className = '' }) => {
  const entry = lookup(term);

  if (!entry) return <span className={className}>{children}</span>;

  const content = (
    <span
      className={`inline-flex items-center gap-0.5 underline decoration-dotted decoration-muted-foreground/60 cursor-help ${className}`}
      data-glossary-term={term}
    >
      {children}
      {showIcon && <HelpCircle className="w-3 h-3 text-muted-foreground" />}
    </span>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{content}</PopoverTrigger>
      <PopoverContent className="w-80 text-xs">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-sm">{entry.title}</p>
              {entry.direction && (
                <p className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                  {directionIcon(entry.direction)} {directionLabel(entry.direction)}
                </p>
              )}
            </div>
          </div>
          <p className="text-muted-foreground font-medium">{entry.short}</p>
          {entry.detail && <p className="leading-relaxed">{entry.detail}</p>}
          {typeof entry.threshold === 'number' && (
            <div className="pt-1 mt-1 border-t text-[11px]">
              <span className="text-muted-foreground">Soglia di promozione: </span>
              <span className="font-mono font-semibold">
                {entry.direction === 'lower_better' ? '≤ ' : '≥ '}
                {Math.abs(entry.threshold) < 1
                  ? (entry.threshold * 100).toFixed(0) + '%'
                  : entry.threshold.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GlossaryTerm;
